// El Brain: le hablas y opera la plataforma por ti.
//
// No confundir con `ragApi`, que responde **desde los documentos**. El Brain
// ejecuta: crea agentes, conecta herramientas, sube documentacion. Hasta el
// 2026-08-24 `BrainPage` llamaba a `ragApi`, o sea que preguntaba al sitio
// equivocado y por eso nunca hacia nada.

import { RAG_API_URL, getAuthToken } from '@/config/env'
import type { MediaType } from '@/lib/document-type'

/** Un agente del despliegue, tal como lo devuelve la API.
 *
 * El prototipo tenia su propio tipo `Agent` con `emoji`, `color` y
 * `accentColor`, que estaban escritos en sus datos de ejemplo. Estos son los
 * campos que la API da de verdad.
 */
export interface AgenteDelBrain {
  id: string
  name: string
  display_name?: string
  description?: string
  is_enabled?: boolean
  tool_count?: number
}

export interface BrainToolCall {
  tool: string
  args?: Record<string, unknown>
  status?: string
  /** Lo que devolvio la llamada. Es lo que hace verificable la respuesta. */
  result?: unknown
  /** Si fallo, por que. */
  error?: string
  // Solo viene cuando `status` es `necesita_confirmacion`: dice que operacion
  // se detuvo y sobre que, para poder preguntarlo con palabras concretas en vez
  // de repetir el texto del modelo.
  accion?: {
    nombre: string
    nombre_visible: string
    argumentos?: Record<string, unknown>
  }
}

/** El estado que el servidor pone cuando la guarda detiene una operacion.
 *
 * Es un dato, no una frase: hasta el 2026-08-25 la unica señal era el texto
 * «DETENIDO...» dentro del resultado, y comparar prosa se rompe en cuanto
 * alguien mejora el mensaje —que ya paso una vez—.
 */
export const NECESITA_CONFIRMACION = 'necesita_confirmacion'

/** La operacion que espera confirmacion, si hay alguna en la respuesta. */
/** The status of a turn stopped to ask the person something (D7). The same
 * field as `NECESITA_CONFIRMACION`, so the same Spanish. */
export const NEEDS_ANSWER = 'necesita_respuesta'

/** A question the Brain stopped to ask: its options, and whether several can
 * be chosen at once. */
export interface PersonQuestion {
  text: string
  options: string[]
  multiple: boolean
}

/** The questions a turn stopped on, read from the call itself: its arguments
 * are the questions exactly as the Brain wrote them. A turn saved before
 * 2026-09-27 asked a single one, as `question` and `options`. */
export function pendingQuestions(calls: BrainToolCall[]): PersonQuestion[] {
  const asked = calls.find((call) => call.status === NEEDS_ANSWER)
  if (!asked) return []
  const args = asked.args ?? {}
  const raw: unknown[] = Array.isArray(args.questions) ? args.questions : [args]
  return raw
    .map((item) => {
      const q = (item ?? {}) as Record<string, unknown>
      const options = Array.isArray(q.options) ? q.options.map(String).filter(Boolean) : []
      return { text: String(q.question ?? ''), options, multiple: Boolean(q.multiple) }
    })
    .filter((q) => q.text)
}

/** What goes back to the Brain: the choices of each question. With several
 * questions each answer carries its own, or the Brain cannot tell them apart. */
export function answerText(questions: PersonQuestion[], answers: string[][]): string {
  if (questions.length === 1) return answers[0].join(', ')
  return questions.map((q, i) => `${q.text} ${answers[i].join(', ')}`).join('\n')
}

export function confirmacionPendiente(
  llamadas: BrainToolCall[],
): BrainToolCall['accion'] | null {
  const detenida = llamadas.find((l) => l.status === NECESITA_CONFIRMACION)
  return detenida?.accion ?? null
}

/** Lo que ha costado el turno, tal como lo devuelve la API.
 *
 * `coste_es_techo` importa mas de lo que parece: parte de la entrada la sirve
 * la cache del proveedor y se factura mas barata, pero el dolar se calcula
 * cobrandolo todo a precio completo. Darlo por exacto seria mentir a la baja al
 * reves: se dice que es un maximo.
 */
export interface CosteDelTurno {
  tokens_entrada: number
  tokens_salida: number
  tokens_total: number
  tokens_entrada_de_cache: number
  coste_usd: number | null
  coste_es_techo: boolean
  llamadas_al_modelo: number
  herramientas_invocadas: number
  herramientas: string[]
  herramientas_con_fallo: number
  por_modelo: Array<{
    proveedor: string
    modelo: string
    llamadas: number
    tokens_entrada: number
    tokens_salida: number
    tokens_de_cache: number
    sin_medir: number
    coste_usd: number | null
  }>
}

export interface EnQueSeBaso {
  /** Los documentos que le llegaron al modelo, el primero delante. */
  documentos: string[]
  /** Por que ese documento y no otro. Vacio cuando no habia con quien
   *  compararlo. */
  porQue: string
}

/** Lo que el turno trae sobre en que se baso, o nada si no se baso en nada.
 *
 * Se devuelve `undefined` cuando no hay documentos, y no un objeto vacio: un
 * turno que no consulto nada -un saludo, una orden de la plataforma- no tiene
 * en que basarse, y enseñar «En que me base: nada» debajo de cada hola es
 * ruido que se deja de leer justo cuando importa.
 */
export function enQueSeBasoDe(estado: unknown): EnQueSeBaso | undefined {
  if (!estado || typeof estado !== 'object') return undefined
  const { context_sources: fuentes, por_que_primero: porQue } = estado as Record<string, unknown>
  // **Una entrada por trozo, no por documento**, y por eso se quitan los
  // repetidos aqui. Medido en un turno real el 2026-09-18: «¿Hay
  // financiacion?» devolvio cinco fuentes y solo tres documentos, con dos de
  // ellos dos veces. Enseñarlo en crudo diria que el agente miro cinco cosas
  // cuando miro tres, que es peor que no decir nada. Se quitan aqui y no en el
  // servidor porque `context_sources` es lo que **le llego al modelo** y
  // conversations tambien lo lee; esto es como se enseña.
  const documentos = Array.isArray(fuentes)
    ? [...new Set(fuentes.map(String).filter(Boolean))]
    : []

  // Solo lo que buscó para esta respuesta. Las memorias del espacio de
  // trabajo le llegan en todos los turnos, pregunte lo que pregunte, y
  // enseñarlas aquí decía «me basé en esto» de cosas que no tenían nada que
  // ver (retirado el 2026-09-27).
  if (documentos.length === 0) return undefined
  return { documentos, porQue: typeof porQue === 'string' ? porQue : '' }
}

const cabeceras = (): Record<string, string> => {
  const token = getAuthToken()
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) h.Authorization = `Bearer ${token}`
  return h
}

// El identificador del Brain se resuelve por su nombre y se recuerda. Meter el
// UUID en el codigo o en una variable de entorno ataria el frontend a la base de
// datos de un entorno concreto: el de dev no es el de produccion.
/** El canal por el que habla esta pantalla. Es uno de los conectados.
 *
 * No se manda `source_key`: `invoke` acepta cualquiera de los dos y exige
 * que coincidan si van los dos, asi que mandar uno solo es una cosa menos
 * que pueda contradecirse.
 */
const CANAL_DE_ESTA_PANTALLA = 'Web'

let idRecordado: string | null = null

export class BrainNoDisponible extends Error {}

async function idDelBrain(): Promise<string> {
  if (idRecordado) return idRecordado

  const respuesta = await fetch(`${RAG_API_URL}/api/v1/rag/agent/v1/agents/`, {
    headers: cabeceras(),
  })
  if (!respuesta.ok) {
    throw new BrainNoDisponible(
      respuesta.status === 401
        ? 'No hay sesion para hablar con el Brain.'
        : 'No se pudo consultar que agentes hay.',
    )
  }

  const cuerpo = await respuesta.json()
  const agentes = cuerpo?.data?.items ?? []
  const brain = agentes.find((a: { name?: string }) => a.name === 'brain')
  if (!brain?.id) {
    throw new BrainNoDisponible(
      'Este despliegue no tiene un agente llamado «brain». Hay que crearlo antes.',
    )
  }

  idRecordado = brain.id as string
  return idRecordado
}

/** Como se llama cada herramienta en palabras, indexado por su nombre tecnico.
 *
 * El rastro de un turno trae `get_api_agent_v1_agents`, que no es algo que se le
 * pueda enseñar a una persona. El nombre legible vive en el catalogo, y solo
 * viaja en el rastro cuando la operacion se detiene a pedir confirmacion. Se
 * pide una vez y se recuerda, como el id del Brain.
 */
let catalogoRecordado: Map<string, string> | null = null

export async function nombresDeHerramientas(): Promise<Map<string, string>> {
  if (catalogoRecordado) return catalogoRecordado

  try {
    const respuesta = await fetch(`${RAG_API_URL}/api/v1/rag/agent/v1/tools/`, {
      headers: cabeceras(),
    })
    if (!respuesta.ok) return new Map()
    const cuerpo = await respuesta.json()
    const items = cuerpo?.data?.items ?? []
    catalogoRecordado = new Map(
      items
        .filter((h: { name?: string }) => h?.name)
        .map((h: { name: string; display_name?: string }) => [h.name, h.display_name || h.name]),
    )
    return catalogoRecordado
  } catch {
    // Sin catalogo se enseña el nombre tecnico, que es feo pero cierto. No
    // merece romper la conversacion.
    return new Map()
  }
}

/** Todos los agentes, para poder elegir a cual hablarle. */
export async function listaDeAgentes(): Promise<AgenteDelBrain[]> {
  const respuesta = await fetch(`${RAG_API_URL}/api/v1/rag/agent/v1/agents/`, {
    headers: cabeceras(),
  })
  if (!respuesta.ok) throw new BrainNoDisponible('No se pudo consultar que agentes hay.')
  const cuerpo = await respuesta.json()
  return (cuerpo?.data?.items ?? []) as AgenteDelBrain[]
}

/** Una conversacion de la lista, sin sus mensajes. */
export interface ConversacionDelBrain {
  id: string
  thread_id: string
  title: string
  updated_at: string
  /** The run it is busy with, or `null`: the same as when it is opened. */
  active_run?: EncargoEnMarcha | null
}

export interface MensajeGuardado {
  id: string
  role: 'user' | 'assistant'
  content: string
  trace?: {
    herramientas?: BrainToolCall[]
    coste?: CosteDelTurno
    /** Lo del estado del turno que dice en que se baso. Se guarda desde que
     *  todo mensaje se contesta en segundo plano: la pantalla lee la respuesta
     *  guardada, y sin esto la perdia siempre. */
    estado?: unknown
  }
  created_at: string
  /** Only on answers: what the Brain did to give it, in order. Empty on answers
   * from before the steps were kept. */
  steps?: RunEvent[]
}

/** Las conversaciones que se han tenido, de la mas reciente a la mas antigua. */
export async function listaDeConversaciones(): Promise<ConversacionDelBrain[]> {
  try {
    const r = await fetch(`${RAG_API_URL}/api/v1/rag/agent/v1/conversations/`, {
      headers: cabeceras(),
    })
    if (!r.ok) return []
    const cuerpo = await r.json()
    return (cuerpo?.data?.items ?? []) as ConversacionDelBrain[]
  } catch {
    // Sin historial se puede seguir conversando: no merece romper la pantalla.
    return []
  }
}

/** Quita un hilo de la lista para que el historial no se llene. */
export async function olvidarConversacion(hilo: string): Promise<void> {
  const r = await fetch(
    `${RAG_API_URL}/api/v1/rag/agent/v1/conversations/${encodeURIComponent(hilo)}/`,
    { method: 'DELETE', headers: cabeceras() },
  )
  if (!r.ok && r.status !== 404) {
    throw new BrainNoDisponible('No se pudo borrar la conversación.')
  }
}

/** El encargo en el que una conversacion sigue ocupada, si hay alguno. */
export interface EncargoEnMarcha {
  id: string
  status: string
  plan?: PlanStep[]
}

/** Una conversacion entera: sus mensajes y, si el Brain sigue trabajando en
 *  ella o esperando respuesta, el encargo con su estado. Es lo que permite
 *  volver despues de recargar y seguir viendola pensar. */
export async function abrirConversacion(
  hilo: string,
): Promise<{ mensajes: MensajeGuardado[]; encargoActivo: EncargoEnMarcha | null; modelo: ChosenModel | null }> {
  // Sin red se abre vacia en vez de romper: se puede seguir escribiendo, y la
  // conversacion sigue en el servidor para la proxima vez.
  const r = await fetch(
    `${RAG_API_URL}/api/v1/rag/agent/v1/conversations/${encodeURIComponent(hilo)}/`,
    { headers: cabeceras() },
  ).catch(() => null)
  if (!r?.ok) return { mensajes: [], encargoActivo: null, modelo: null }
  const datos = (await r.json())?.data ?? {}
  return {
    mensajes: (datos.messages ?? []) as MensajeGuardado[],
    encargoActivo: (datos.active_run ?? null) as EncargoEnMarcha | null,
    // El que se eligió en ella, para seguir con el mismo al volver (7.3).
    modelo: (datos.chosen_model ?? null) as ChosenModel | null,
  }
}

/** Los mensajes de una, para repintarla tal como fue. */
export async function leerConversacion(hilo: string): Promise<MensajeGuardado[]> {
  return (await abrirConversacion(hilo)).mensajes
}


/** Un encargo que dura: lo que la API devuelve al pedirlo y al mirarlo.
 *
 * Los nombres son los de la API, que es donde se decidieron. Traducirlos aqui
 * obligaria a mantener dos vocabularios para lo mismo.
 */
export interface RunStep {
  number: number
  label: string
  result: {
    response_text?: string
    error?: string
    stopped?: string
    // Lo que llamo para llegar ahi, con el mismo recorte que el historial del
    // chat. Un encargo que hace doce consultas y no las enseña se lee como si
    // se las hubiera inventado.
    tools?: BrainToolCall[]
  }
  finished_at: string | null
}

/** Algo que el Brain hizo mientras trabajaba: una respuesta del modelo o una
 * herramienta que empezó o terminó. Llegan en orden, mientras piensa. */
export interface RunEvent {
  kind: 'llm.response' | 'tool_call.started' | 'tool_call.finished' | 'tool_call.error'
  tool_name: string
  status: string
  latency_ms: number | null
  /** Lo que pensó antes de esa respuesta, ya en el idioma de la aplicación.
   * Vacío si el proveedor no lo mandó, o mientras se traduce. */
  thinking: string
  at: string
}

/** One step of an errand's plan, as the Brain writes it (brain-run-bucle). */
export interface PlanStep {
  title: string
  status: 'done' | 'active' | 'pending'
}

/** Which step of the plan it is at: the active one, or the last done. */
export function planProgress(plan?: PlanStep[]): { n: number; total: number } | null {
  if (!plan?.length) return null
  const active = plan.findIndex((s) => s.status === 'active')
  const done = plan.filter((s) => s.status === 'done').length
  return { n: active >= 0 ? active + 1 : Math.max(done, 1), total: plan.length }
}

export interface Run {
  id: string
  status: string
  goal: string
  /** The plan the Brain wrote for work that does not fit in one answer. */
  plan?: PlanStep[]
  created_at?: string
  finished_at: string | null
  spent?: { tokens: number; usd: number | null }
  limits?: { tokens: number; minutes: number; usd: number }
  steps: RunStep[]
  events?: RunEvent[]
}

/** Lo que el Brain está haciendo en el paso en marcha.
 *
 * Son los eventos posteriores al último paso terminado: un encargo que se paró
 * a preguntar y se contestó ya trae los de la pregunta anterior, y esos son de
 * la respuesta de antes, no de la que está pensando.
 */
export function eventsOfRunningStep(run: Run): RunEvent[] {
  // El canal manda las horas con espacio (`2026-09-26 15:09:25.24+00:00`) y
  // el detalle con `T`: se comparan como fechas, no como texto.
  const time = (text: string) => new Date(text.replace(' ', 'T')).getTime()
  const finished = run.steps.filter((step) => step.finished_at)
  const since = finished.length ? time(finished[finished.length - 1].finished_at!) : -Infinity
  return (run.events ?? []).filter((event) => time(event.at) > since)
}

/** Estados de los que un encargo no vuelve solo. Los mismos que el servidor.
 *
 * Se repiten aqui porque la pantalla decide con ellos si sigue mirando, y
 * pedirselos al servidor para saber si hay que preguntarle otra vez seria dar
 * una vuelta para llegar al mismo sitio.
 */
export const RUN_IS_OVER = new Set([
  'done',
  'failed',
  'cancelled',
  'stopped_by_budget',
])

/** La conversacion ya esta trabajando en otro mensaje (409). Lleva el
 *  encargo que sigue en marcha, para seguirlo en vez de quedarse sin nada. */
export class ConversacionOcupada extends BrainNoDisponible {
  readonly runId: string | null

  constructor(runId: string | null) {
    super('Todavia estoy con tu mensaje anterior en esta conversacion.')
    this.runId = runId
  }
}

/** A model picked for a conversation, instead of its agent's (D9). */
export interface ChosenModel {
  provider: string
  model: string
}

/** Lo que viaja con un mensaje: dónde está la persona, lo que aprobó y, si lo
 * eligió, el modelo. **El modelo va en cada mensaje, también al contestar**:
 * el encargo toma lo de la última palabra de la persona, así que un paso tras
 * una confirmación volvería al modelo del agente sin esto. */
function contextoDelMensaje(
  donde?: { pagina: string; sobre?: { tipo: string; id: string }; idioma?: string },
  accionesConfirmadas: string[] = [],
  modelo?: ChosenModel | null,
  adjunto?: Adjunto,
) {
  return {
    ...(modelo ? { provider: modelo.provider, model: modelo.model } : {}),
    source: CANAL_DE_ESTA_PANTALLA,
    metadata: {
      ...(donde?.pagina ? { donde } : {}),
      ...(accionesConfirmadas.length ? { acciones_confirmadas: accionesConfirmadas } : {}),
      ...(adjunto ? { adjunto } : {}),
    },
  }
}

/** Lo que se le pasó al Brain con el clip, sin archivarlo (brain-multimodal,
 *  D3): qué era y lo que costó entenderlo, que el turno suma a su coste (R7). */
export interface Adjunto {
  tipo: MediaType | 'document'
  nombre: string
  consumo?: unknown
}

/** No se pudo entender el adjunto. `code` es el del servidor
 *  (`UNSUPPORTED_MEDIA_CAPABILITY`, `MISSING_PROVIDER_CREDENTIAL`,
 *  `ENRICHMENT_ERROR`), para decir por qué y no un «algo falló» (D5). */
export class AdjuntoNoEntendido extends Error {
  readonly code: string

  constructor(message: string, code: string) {
    super(message)
    this.name = 'AdjuntoNoEntendido'
    this.code = code
  }
}

async function enBase64(archivo: File): Promise<string> {
  const bytes = new Uint8Array(await archivo.arrayBuffer())
  let binario = ''
  // A trozos: `String.fromCharCode(...bytes)` entero revienta la pila con un
  // vídeo de 30 MB.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binario += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  }
  return btoa(binario)
}

/**
 * Describe una imagen o un vídeo, o transcribe un audio, una sola vez (D3).
 *
 * Es el mismo endpoint que usan los canales de clientes con lo que les llega
 * por WhatsApp. Lo que devuelve viaja como texto en el mensaje: el modelo no
 * vuelve a ver la imagen en cada vuelta, que es lo que la haría cara.
 */
export async function describirAdjunto(
  archivo: File,
  media: { type: MediaType | 'document'; mime: string },
  agenteId?: string,
): Promise<{ texto: string; adjunto: Adjunto }> {
  const id = agenteId ?? (await idDelBrain())
  const respuesta = await fetch(`${RAG_API_URL}/api/v1/rag/agent/v1/agents/${id}/media/enrich/`, {
    method: 'POST',
    headers: cabeceras(),
    body: JSON.stringify({
      attachment_type: media.type,
      mime_type: media.mime,
      filename: archivo.name,
      file_bytes_b64: await enBase64(archivo),
    }),
  })
  const cuerpo = await respuesta.json().catch(() => ({}))
  if (!respuesta.ok) {
    const error = (cuerpo as { error?: { code?: string; message?: string } }).error ?? {}
    throw new AdjuntoNoEntendido(error.message ?? `HTTP ${respuesta.status}`, error.code ?? 'ENRICHMENT_ERROR')
  }
  const datos = ((cuerpo as { data?: Record<string, unknown> }).data ?? {}) as {
    ai_description?: string
    transcription?: string
    enrichment_meta?: { consumo?: unknown }
  }
  return {
    texto: String(datos.transcription ?? datos.ai_description ?? '').trim(),
    adjunto: { tipo: media.type, nombre: archivo.name, consumo: datos.enrichment_meta?.consumo },
  }
}

/** Mandar un mensaje: se guarda y se trabaja en segundo plano.
 *
 * Devuelve el encargo que lo contesta, no la respuesta. La respuesta aparece
 * en la conversacion cuando termina, aunque entretanto se recargue la pagina.
 */
export async function enviarMensaje(
  mensaje: string,
  hilo: string,
  {
    agenteId,
    donde,
    modelo,
    adjunto,
  }: {
    agenteId?: string
    donde?: { pagina: string; sobre?: { tipo: string; id: string }; idioma?: string }
    modelo?: ChosenModel | null
    adjunto?: Adjunto
  } = {},
): Promise<string> {
  const id = agenteId ?? (await idDelBrain())
  const respuesta = await fetch(`${RAG_API_URL}/api/v1/rag/agent/v1/agents/${id}/runs/`, {
    method: 'POST',
    headers: cabeceras(),
    body: JSON.stringify({ message: mensaje, thread_id: hilo, ...contextoDelMensaje(donde, [], modelo, adjunto) }),
  })
  if (respuesta.status === 409) {
    const cuerpo = await respuesta.json().catch(() => ({}))
    throw new ConversacionOcupada(cuerpo?.error?.details?.run_id ?? null)
  }
  if (!respuesta.ok) {
    throw new BrainNoDisponible(`El Brain respondio ${respuesta.status}.`)
  }
  return String((await respuesta.json()).data.id)
}

/** Contestar a un encargo que se paro a preguntar.
 *
 * Confirmar es mandar el nombre de la accion en `accionesConfirmadas`: la
 * guarda lee eso y no el texto, asi que el «si» sin la accion no aprueba nada.
 */
export async function contestarEncargo(
  runId: string,
  mensaje: string,
  accionesConfirmadas: string[] = [],
  donde?: { pagina: string; sobre?: { tipo: string; id: string }; idioma?: string },
  modelo?: ChosenModel | null,
  adjunto?: Adjunto,
): Promise<void> {
  const respuesta = await fetch(`${RAG_API_URL}/api/v1/rag/agent/v1/runs/${runId}/answer/`, {
    method: 'POST',
    headers: cabeceras(),
    body: JSON.stringify({ message: mensaje, ...contextoDelMensaje(donde, accionesConfirmadas, modelo, adjunto) }),
  })
  if (!respuesta.ok) {
    throw new BrainNoDisponible(`El Brain respondio ${respuesta.status}.`)
  }
}

export async function getRun(runId: string): Promise<Run> {
  const respuesta = await fetch(
    `${RAG_API_URL}/api/v1/rag/agent/v1/runs/${runId}/`,
    { headers: cabeceras() },
  )
  if (!respuesta.ok) {
    throw new BrainNoDisponible('No se pudo consultar el encargo.')
  }
  return (await respuesta.json()).data as Run
}

export async function cancelRun(runId: string): Promise<Run> {
  const response = await fetch(
    `${RAG_API_URL}/api/v1/rag/agent/v1/runs/${runId}/cancel/`,
    { method: 'POST', headers: cabeceras() },
  )
  if (!response.ok) {
    throw new BrainNoDisponible('No se pudo cancelar el encargo.')
  }
  return (await response.json()).data as Run
}
